import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "services/supabase";

const initialState = {
  subscription: null,
  hasSubscription: false,
  allowCheckout: false,
  loading: false,
  error: null,
  loaded: false,
  products: [],
  productsLoading: false,
  productsError: null,
  productsLoaded: false,
  featureTiers: null,
  featureTiersLoading: false,
  featureTiersError: null,
  featureTiersLoaded: false,
  portalLoading: false,
  portalError: null,
  checkoutSession: null,
  checkoutLoading: false,
  checkoutError: null,
  redeemLoading: false,
  redeemError: null,
  redeemSuccess: null,
};

export const fetchCurrentStripeSubscription = createAsyncThunk(
  "stripe/fetchCurrentSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/current`;

      const response = await fetch(endpoint, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (response.status === 404) {
        return {
          subscription: null,
          hasSubscription: false,
          allowCheckout: false,
        };
      }

      if (!response.ok) {
        const message = `Failed to load subscription: ${response.status}`;
        return rejectWithValue(message);
      }

      const data = await response.json();
      let subscription = null;
      let hasSubscription = false;
      let allowCheckout = false;

      if (data && typeof data === "object") {
        const {
          subscription: subscriptionPayload,
          hasSubscription: hasSubscriptionField,
          allowCheckout: allowCheckoutField,
          ...rest
        } = data;

        if (subscriptionPayload) {
          subscription = subscriptionPayload;
        } else if (hasSubscriptionField) {
          const meaningfulKeys = Object.keys(rest).filter((key) => rest[key] !== undefined);
          if (meaningfulKeys.length > 0) {
            subscription = rest;
          }
        }

        hasSubscription = Boolean(
          hasSubscriptionField ?? (subscription ? true : false)
        );
        allowCheckout = Boolean(allowCheckoutField ?? false);
      } else {
        subscription = data;
        hasSubscription = Boolean(subscription);
      }

      return {
        subscription,
        hasSubscription,
        allowCheckout,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load subscription");
    }
  }
);

export const fetchStripeProducts = createAsyncThunk(
  "stripe/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/products`;

      const response = await fetch(endpoint, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (!response.ok) {
        return rejectWithValue(`Failed to load products: ${response.status}`);
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        return data;
      }
      if (Array.isArray(data?.products)) {
        return data.products;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load products");
    }
  }
);

export const fetchStripeFeatures = createAsyncThunk(
  "stripe/fetchFeatures",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { stripeSubscriptionReducer } = getState();
      if (stripeSubscriptionReducer.featureTiers) {
        return stripeSubscriptionReducer.featureTiers;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/features`;

      const response = await fetch(endpoint, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (!response.ok) {
        return rejectWithValue(`Failed to load features: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load features");
    }
  },
  {
    condition: (_, { getState }) => {
      const { stripeSubscriptionReducer } = getState();
      if (stripeSubscriptionReducer.featureTiersLoading) {
        return false;
      }
      if (stripeSubscriptionReducer.featureTiersLoaded) {
        return false;
      }
      return true;
    },
  }
);

export const fetchStripePortalUrl = createAsyncThunk(
  "stripe/fetchPortalUrl",
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/portal`;

      const response = await fetch(endpoint, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (!response.ok) {
        return rejectWithValue(`Failed to load portal: ${response.status}`);
      }

      const data = await response.json();
      if (data?.url) {
        window.location.href = data.url;
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to open customer portal");
    }
  }
);

export const createStripeCheckoutSession = createAsyncThunk(
  "stripe/createCheckoutSession",
  async ({ priceId } = {}, { rejectWithValue }) => {
    try {
      if (!priceId) {
        return rejectWithValue("priceId is required to start checkout");
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/checkout`

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({ priceId }),
      });

      if (!response.ok) {
        return rejectWithValue(`Failed to start checkout: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to start checkout");
    }
  }
);

export const redeemStripeCode = createAsyncThunk(
  "stripe/redeemCode",
  async ({ code } = {}, { rejectWithValue }) => {
    try {
      if (!code) {
        return rejectWithValue("A code is required to redeem.");
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
      const endpoint = `${baseUrl}/subscription/redeem`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const message = await response.text();
        return rejectWithValue(
          message || `Failed to redeem code: ${response.status}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to redeem code");
    }
  }
);

const StripeSlice = createSlice({
  name: "stripeSubscription",
  initialState,
  reducers: {
    clearStripeSubscription: (state) => {
      state.subscription = null;
      state.hasSubscription = false;
      state.allowCheckout = false;
      state.loading = false;
      state.error = null;
      state.loaded = false;
      state.products = [];
      state.productsLoading = false;
      state.productsError = null;
      state.productsLoaded = false;
      state.featureTiers = null;
      state.featureTiersLoading = false;
      state.featureTiersError = null;
      state.featureTiersLoaded = false;
      state.checkoutSession = null;
      state.checkoutLoading = false;
      state.checkoutError = null;
    },
    clearStripeCheckoutState: (state) => {
      state.checkoutSession = null;
      state.checkoutLoading = false;
      state.checkoutError = null;
    },
    clearStripeRedeemState: (state) => {
      state.redeemLoading = false;
      state.redeemError = null;
      state.redeemSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentStripeSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentStripeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload?.subscription || null;
        state.hasSubscription = Boolean(action.payload?.hasSubscription);
        state.allowCheckout = Boolean(action.payload?.allowCheckout);
        state.loaded = true;
        state.error = null;
      })
      .addCase(fetchCurrentStripeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
        state.subscription = null;
        state.hasSubscription = false;
        state.allowCheckout = false;
        state.loaded = true;
      })
    .addCase(fetchStripeProducts.pending, (state) => {
      state.productsLoading = true;
      state.productsError = null;
    })
    .addCase(fetchStripeProducts.fulfilled, (state, action) => {
      state.productsLoading = false;
      state.products = action.payload;
      state.productsLoaded = true;
      state.productsError = null;
    })
    .addCase(fetchStripeProducts.rejected, (state, action) => {
      state.productsLoading = false;
      state.productsError = action.payload || action.error.message;
      state.productsLoaded = false;
    })
    .addCase(fetchStripeFeatures.pending, (state) => {
      state.featureTiersLoading = true;
      state.featureTiersError = null;
    })
    .addCase(fetchStripeFeatures.fulfilled, (state, action) => {
      state.featureTiersLoading = false;
      state.featureTiers = action.payload || {};
      state.featureTiersError = null;
      state.featureTiersLoaded = true;
    })
    .addCase(fetchStripeFeatures.rejected, (state, action) => {
      state.featureTiersLoading = false;
      state.featureTiersError = action.payload || action.error.message;
      state.featureTiersLoaded = false;
    })
    .addCase(fetchStripePortalUrl.pending, (state) => {
      state.portalLoading = true;
      state.portalError = null;
    })
    .addCase(fetchStripePortalUrl.fulfilled, (state) => {
      state.portalLoading = false;
      state.portalError = null;
    })
    .addCase(fetchStripePortalUrl.rejected, (state, action) => {
      state.portalLoading = false;
      state.portalError = action.payload || action.error.message;
    })
    .addCase(createStripeCheckoutSession.pending, (state) => {
      state.checkoutLoading = true;
      state.checkoutError = null;
      state.checkoutSession = null;
    })
    .addCase(createStripeCheckoutSession.fulfilled, (state, action) => {
      state.checkoutLoading = false;
      state.checkoutError = null;
      state.checkoutSession = action.payload || null;
    })
    .addCase(createStripeCheckoutSession.rejected, (state, action) => {
      state.checkoutLoading = false;
      state.checkoutError = action.payload || action.error.message;
      state.checkoutSession = null;
    })
    .addCase(redeemStripeCode.pending, (state) => {
      state.redeemLoading = true;
      state.redeemError = null;
      state.redeemSuccess = null;
    })
    .addCase(redeemStripeCode.fulfilled, (state, action) => {
      state.redeemLoading = false;
      state.redeemError = null;
      state.redeemSuccess =
        action.payload?.message || "Code redeemed successfully.";
    })
    .addCase(redeemStripeCode.rejected, (state, action) => {
      state.redeemLoading = false;
      state.redeemError = action.payload || action.error.message;
      state.redeemSuccess = null;
    });
  },
});

export const selectStripeSubscription = (state) => state.stripeSubscriptionReducer.subscription;
export const selectStripeHasSubscription = (state) => state.stripeSubscriptionReducer.hasSubscription;
export const selectStripeAllowCheckout = (state) => state.stripeSubscriptionReducer.allowCheckout;
export const selectStripeSubscriptionLoading = (state) =>
  state.stripeSubscriptionReducer.loading;
export const selectStripeSubscriptionLoaded = (state) =>
  state.stripeSubscriptionReducer.loaded;
export const selectStripeSubscriptionError = (state) =>
  state.stripeSubscriptionReducer.error;
export const selectStripeProducts = (state) =>
  state.stripeSubscriptionReducer.products;
export const selectStripeProductsLoading = (state) =>
  state.stripeSubscriptionReducer.productsLoading;
export const selectStripeProductsLoaded = (state) =>
  state.stripeSubscriptionReducer.productsLoaded;
export const selectStripeProductsError = (state) =>
  state.stripeSubscriptionReducer.productsError;
export const selectStripeFeatureTiers = (state) =>
  state.stripeSubscriptionReducer.featureTiers;
export const selectStripeFeatureTiersLoading = (state) =>
  state.stripeSubscriptionReducer.featureTiersLoading;
export const selectStripeFeatureTiersError = (state) =>
  state.stripeSubscriptionReducer.featureTiersError;
export const selectStripePortalLoading = (state) =>
  state.stripeSubscriptionReducer.portalLoading;
export const selectStripePortalError = (state) =>
  state.stripeSubscriptionReducer.portalError;
export const selectStripeCheckoutLoading = (state) =>
  state.stripeSubscriptionReducer.checkoutLoading;
export const selectStripeCheckoutError = (state) =>
  state.stripeSubscriptionReducer.checkoutError;
export const selectStripeCheckoutSession = (state) =>
  state.stripeSubscriptionReducer.checkoutSession;
export const selectStripeRedeemLoading = (state) =>
  state.stripeSubscriptionReducer.redeemLoading;
export const selectStripeRedeemError = (state) =>
  state.stripeSubscriptionReducer.redeemError;
export const selectStripeRedeemSuccess = (state) =>
  state.stripeSubscriptionReducer.redeemSuccess;

export const {
  clearStripeSubscription,
  clearStripeCheckoutState,
  clearStripeRedeemState,
} = StripeSlice.actions;

export default StripeSlice.reducer;

